import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import '@templates/gcsp/styles/styles.css';
import TableHeader from '@molecules/gcsp/components/tableHeader';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { LicenseManager } from 'ag-grid-enterprise';
import {
  getGCSPPastOpportunitiesMonthly,
  getGCSPPastOpportunitiesQuaterly,
} from '@services/gcspServices';
import { AG_GRID_LICENSE_KEY } from '@constants/licenseKeys';
import { useRecoilState } from 'recoil';
import { Gcsp_Past_Opp_View_State } from '@recoil/recoil';

import { MotifChip } from '@ey-xd/motif-react';
import wonIcon from '@assets/icons/medal.png';
import openIcon from '@assets/icons/dashed_circle.png';
import lostIcon from '@assets/icons/block_circle.png';
import {
  TableData1C,
  TableData1CBold,
  GcspPastStatusCell,
  PursuitTypeCell,
} from '@atoms/tableData/styles/tableData.styles';

import {
  ABBREVIATED_MONTH_LIST,
  QUARTERLY_DETAILS,
  TABLE_HEADER_SUBTEXT,
} from '@constants/commonConstants';
import { DateFiltering, OppsNameSorting, logError } from '@util/commonFunctions';

import {
  GcspPastTableContainer,
  GcspPastFooterTableContainer,
  TableData1BDark,
  TableData1CDark,
  GcspFooterColumnRenderer,
} from '@atoms/gcsp/gcspTable.styles';
import { formateTOV } from '@util/commonUtil';

LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
let gridApi; // eslint-disable-line

const GcspPastTable = props => {
  const topGrid = useRef(null);
  const bottomGrid = useRef(null);

  const TYPE = props.forecast_type;
  const EMAIL = props.email;
  const MONTH = props.month;
  const YEAR = props.year;
  const ACCOUNT_ID = props.account;
  const QUARTER = props.quarter;
  const [gcspOppViewData, setGcspOppViewData] = useRecoilState(Gcsp_Past_Opp_View_State);
  const [rowData, setRowData] = useState([]);
  const [showNowRowsToShowText, setShowNowRowsToShowText] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comment, setComment] = useState('');

  // Determine the display title based on forecast type
  const getDisplayTitle = () => {
    if (TYPE === 'quarter') {
      // Check if QUARTER already starts with 'Q', if not, add it
      return QUARTER?.toString().startsWith('Q') ? QUARTER : `Q${QUARTER}`;
    }
    return MONTH;
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    setRowData([]);
    setShowNowRowsToShowText(showNowRowsToShowText);

    const fetchData = async () => {
      try {
        const getOpportunitiesDetailsAPI =
          TYPE === 'month'
            ? await getGCSPPastOpportunitiesMonthly(EMAIL, ACCOUNT_ID, MONTH, YEAR, TYPE, QUARTER)
            : TYPE === 'quarter'
              ? await getGCSPPastOpportunitiesQuaterly(
                  EMAIL,
                  ACCOUNT_ID,
                  MONTH,
                  YEAR,
                  TYPE,
                  QUARTER,
                )
              : null;

        if (getOpportunitiesDetailsAPI) {
          // Set the comment field from the API response (preserve null/undefined)
          // Extract the comment field from Your_Judge[0]
          const apiComment = getOpportunitiesDetailsAPI?.Your_Judge?.[0]?.comment ?? null;
          setComment(apiComment);
          setGcspOppViewData(getOpportunitiesDetailsAPI);
          const opppData = getOpportunitiesDetailsAPI.Committed.concat(
            getOpportunitiesDetailsAPI.Not_Committed,
            getOpportunitiesDetailsAPI.Missed,
          );
          setShowNowRowsToShowText(false);
          setRowData(opppData);
          // To show text 'No rows to show'
        }
      } catch (error) {
        logError('getGCSPPastOpportunitiesMonthly / getGCSPPastOpportunitiesQuaterly', error);
        // To show text 'No rows to show'
        setShowNowRowsToShowText(false);
      }
    };
    fetchData();
  }, [TYPE, EMAIL, MONTH, YEAR, ACCOUNT_ID, QUARTER]);

  const getRowStyle = params => {
    // if (params.node.key == 'Committed' || params.node.parent.key == 'Committed') {
    if (params.node.group === true) {
      // For row groups
      return { background: '#ffffff' };
    } else {
      // For inner elements
      return { background: '#eaeaf2' };
    }
  };
  const OpportunityNameCellRenderer = ({ value }) => {
    const formattedValue = value ? value.toString() : '';

    if (formattedValue.length > 20) {
      return (
        <TableData1C>
          <span title={formattedValue}>{formattedValue.slice(0, 20)}...</span>
        </TableData1C>
      );
    }

    return <TableData1C>{formattedValue}</TableData1C>;
  };

  const TextCellRendererCBold = ({ value }) => <TableData1CBold>{value}</TableData1CBold>;

  const PursuitTypeCellRenderer = ({ value }) => {
    // Convert pursuit types to uppercase(Brand New) for display
    const displayValue = value
      ? value
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : '';

    return <PursuitTypeCell>{displayValue}</PursuitTypeCell>;
  };
  PursuitTypeCellRenderer.propTypes = {
    value: PropTypes.string,
  };

  const CommitStatusCellRenderer = props => {
    if (props.value === 'Committed') {
      return (
        <MotifChip variant="success" type="dot">
          {QUARTERLY_DETAILS.COMMITTED}
        </MotifChip>
      );
    } else if (props.value === 'Not Committed') {
      return (
        <MotifChip variant="disabled" type="dot">
          {QUARTERLY_DETAILS.NOT_COMMITTED}
        </MotifChip>
      );
    } else {
      return (
        <MotifChip variant="warning" type="dot">
          {QUARTERLY_DETAILS.MISSED}
        </MotifChip>
      );
    }
  };

  const StatusFilter = params => {
    if (params.data && params.data.Status !== undefined) {
      if (params.data.Status === 0) {
        return QUARTERLY_DETAILS.OPEN;
      } else if (params.data.Status === 1) {
        return QUARTERLY_DETAILS.WON;
      } else if (params.data.Status === 2) {
        return QUARTERLY_DETAILS.LOST;
      } else if (params.data.Status === 3) {
        return QUARTERLY_DETAILS.DECLINED;
      } else {
        return '';
      }
    }
    return '';
  };

  const StatusCellRenderer = props => {
    if (props.node.group === true) {
      let count = 0;
      for (let i = 0; i < props.node.childrenAfterAggFilter.length; i++) {
        if (props.node.childrenAfterAggFilter[i].data.Status === 1) {
          count++;
        }
      }
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspPastStatusCell>
            <img src={wonIcon} width={18} height={18} alt="" />
            <TableData1C>{count}</TableData1C>
            <TableData1C>{QUARTERLY_DETAILS.WON}</TableData1C>
          </GcspPastStatusCell>
        </div>
      );
    }
    if (props.value === 0) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspPastStatusCell>
            <img src={openIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.OPEN}</TableData1C>
          </GcspPastStatusCell>
        </div>
      );
    } else if (props.value === 1) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspPastStatusCell>
            <img src={wonIcon} width={18} height={18} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.WON}</TableData1C>
          </GcspPastStatusCell>
        </div>
      );
    } else if (props.value === 2) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspPastStatusCell>
            <img src={lostIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.LOST}</TableData1C>
          </GcspPastStatusCell>
        </div>
      );
    } else if (props.value === 3) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspPastStatusCell>
            <img src={lostIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.DECLINED}</TableData1C>
          </GcspPastStatusCell>
        </div>
      );
    } else {
      return <div></div>;
    }
  };

  const DateCellRenderer = props => {
    if (props.node.group !== true) {
      const date = props.value;
      const month = props.Month;

      if (date === null) {
        return <TableData1C>-</TableData1C>;
      }
      if (date === undefined) {
        return <TableData1C></TableData1C>;
      } else {
        const month_array = ABBREVIATED_MONTH_LIST;

        let isoDate = new Date(date);
        const timeZoneOffsetMinutes = isoDate.getTimezoneOffset() * 60000;
        isoDate = new Date(isoDate.getTime() + timeZoneOffsetMinutes);

        const derivedMonth = month_array[isoDate.getMonth()];
        let derivedDate = isoDate.getDate();
        const derivedYear = isoDate.getFullYear();
        if (derivedDate < 10) {
          derivedDate = `0${derivedDate}`;
        }
        const final_date = `${derivedDate} ${derivedMonth} ${derivedYear} (${month}) `;
        return <TableData1C>{final_date}</TableData1C>;
      }
    }
    return <TableData1C> </TableData1C>;
  };

  const PursuitLeaderCellRenderer = ({ value }) => {
    const formattedValue = value ? value.toString() : '';

    if (formattedValue.length > 15) {
      return (
        <TableData1C>
          <span title={formattedValue}>{formattedValue.slice(0, 15)}...</span>
        </TableData1C>
      );
    }

    return <TableData1C>{formattedValue}</TableData1C>;
  };

  const MoneyCellRenderer2 = props => {
    const service_line = props.service_line;
    const service_line_latest = props.service_line_latest;
    let new_value = 0;
    let old_value = 0;

    if (props.node.group === true) {
      for (let i = 0; i < props.node.childrenAfterAggFilter.length; i++) {
        new_value += props.node.childrenAfterAggFilter[i].data[service_line_latest];
        old_value += props.node.childrenAfterAggFilter[i].data[service_line];
      }
    } else {
      old_value = props.node.data[service_line];
      new_value = props.node.data[service_line_latest];
    }

    const formattedValue1 = Number(new_value).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    const formattedValue2 = Number(old_value).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    if (formattedValue1.length > 12 || formattedValue2.length > 12) {
      return (
        <TableData1C>
          <span title={`${formattedValue1} (${formattedValue2})`}>
            {formattedValue1.slice(0, 12)}... (<s>{formattedValue2.slice(0, 12)}...</s>)
          </span>
        </TableData1C>
      );
    }

    // Existing rendering logic
    if (formattedValue1 === formattedValue2) {
      return <TableData1C>{formattedValue1}</TableData1C>;
    } else {
      return (
        <TableData1C>
          {formattedValue1} (<s>{formattedValue2}</s>)
        </TableData1C>
      );
    }
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'PL Commit Status',
      field: 'category',
      minWidth: 200,
      headerComponentParams: {
        enableMenu: false,
        enableSorting: false,
      },
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: CommitStatusCellRenderer,
      },
      suppressMovable: true,
    };
  }, []);

  const menuTabsItems = ['filterMenuTab'];

  const columnDefs = [
    {
      headerName: 'PL Commit Status',
      showRowGroup: true,
      rowGroup: true,
      hide: true,
      field: 'category',
      cellRenderer: CommitStatusCellRenderer,
      width: 100,
      suppressMovable: true,
    },
    {
      headerName: 'Status',
      field: 'Status',
      headerComponentParams: {
        enableSorting: false,
        enableMenu: true,
      },
      cellRenderer: StatusCellRenderer,
      width: 110,
      menuTabs: menuTabsItems,
      filter: 'agTextColumnFilter', // eslint-disable-line
      suppressMovable: true,
      filterValueGetter: StatusFilter,
    },
    {
      headerName: 'ID',
      field: 'ey_id',
      filter: true,
      headerComponentParams: {
        enableSorting: false,
        enableMenu: true,
      },
      cellRenderer: TextCellRendererCBold,
      width: 100,
      menuTabs: menuTabsItems,
      filter: 'agTextColumnFilter', // eslint-disable-line
      suppressMovable: true,
    },

    {
      headerName: 'Pursuit Type',
      field: 'pursuit_type',
      filter: true,
      headerComponentParams: {
        enableSorting: true,
        enableMenu: true,
      },
      cellRenderer: 'pursuitTypeCellRenderer',
      width: 170,
      menuTabs: menuTabsItems,
      filter: 'agTextColumnFilter', // eslint-disable-line
      suppressMovable: true,
    },

    {
      headerName: 'Opportunity Name',
      field: 'opportunityname',
      filter: true,
      headerComponentParams: {
        enableSorting: true,
        enableMenu: true,
      },
      width: 210,
      cellRenderer: OpportunityNameCellRenderer,
      menuTabs: menuTabsItems,
      filter: 'agTextColumnFilter', // eslint-disable-line
      suppressMovable: true,
      comparator: OppsNameSorting,
    },

    {
      headerName: 'Eff. Close Date',
      field: 'ECD',
      sortable: true,
      filter: true,
      cellRenderer: DateCellRenderer,
      headerComponentParams: {
        enableSorting: true,
        subText: TABLE_HEADER_SUBTEXT.DATE_FISCAL_MONTH,
      },
      cellRendererParams: params => {
        if (params.node.group !== true) {
          const Month = params.data.ECD_month;
          return { Date, Month };
        }
      },
      width: 180,
      menuTabs: menuTabsItems,
      filter: 'agDateColumnFilter', // eslint-disable-line
      filterParams: DateFiltering,
      suppressMovable: true,
    },
    {
      headerName: 'AWD',
      field: 'AWD',
      sortable: true,
      filter: true,
      cellRenderer: DateCellRenderer,
      headerComponentParams: {
        enableSorting: true,
        subText: TABLE_HEADER_SUBTEXT.DATE_FISCAL_MONTH,
      },
      cellRendererParams: params => {
        if (params.node.group !== true) {
          const Month = params.data.AWD_month;
          return { Date, Month };
        }
      },
      width: 165,
      menuTabs: menuTabsItems,
      filter: 'agDateColumnFilter', // eslint-disable-line
      filterParams: DateFiltering,
      suppressMovable: true,
    },
    {
      headerName: 'Pursuit Leader',
      field: 'PursuitLeader',
      filter: true,
      headerComponentParams: {
        enableSorting: false,
        enableMenu: true,
      },
      width: 160,
      menuTabs: menuTabsItems,
      filter: 'agTextColumnFilter', // eslint-disable-line
      cellRenderer: PursuitLeaderCellRenderer,
      suppressMovable: true,
    },
    {
      headerName: 'Assurance',
      field: 'Assurance_latest',
      sortable: true,
      headerComponentParams: {
        displayName: 'Assurance',
        enableSorting: true,
        enableMenu: false,
        subText: TABLE_HEADER_SUBTEXT.EXCL_ACR,
        subText2: TABLE_HEADER_SUBTEXT.ACTUAL_FORECASTED,
      },
      width: 160,
      cellStyle: { textAlign: 'right' },
      cellClass: 'cell-text-aligment',
      cellRenderer: MoneyCellRenderer2,
      cellRendererParams: () => {
        const service_line_latest = 'Assurance_latest';
        const service_line = 'Assurance';
        return { service_line_latest, service_line };
      },
      suppressMovable: true,
    },
    {
      headerName: 'Consulting',
      field: 'consulting_latest',
      sortable: true,
      headerComponentParams: {
        displayName: 'Consulting',
        enableSorting: true,
        enableMenu: false,
        subText: TABLE_HEADER_SUBTEXT.ACTUAL_FORECASTED,
      },
      width: 160,
      cellStyle: { textAlign: 'right' },
      cellClass: 'cell-text-aligment',
      cellRenderer: MoneyCellRenderer2,
      cellRendererParams: () => {
        const service_line_latest = 'consulting_latest';
        const service_line = 'consulting';
        return { service_line_latest, service_line };
      },
      suppressMovable: true,
    },
    {
      headerName: 'SAT',
      field: 'sat_latest',
      sortable: true,
      headerComponentParams: {
        displayName: 'SAT',
        enableSorting: true,
        enableMenu: false,
        subText: TABLE_HEADER_SUBTEXT.EXCL_ITTS,
        subText2: TABLE_HEADER_SUBTEXT.ACTUAL_FORECASTED,
      },
      width: 160,
      cellClass: 'cell-text-aligment',
      cellRenderer: MoneyCellRenderer2,
      cellRendererParams: () => {
        const service_line_latest = 'sat_latest';
        const service_line = 'sat';
        return { service_line_latest, service_line };
      },
      suppressMovable: true,
    },
    {
      headerName: 'Tax',
      field: 'tax_latest',
      sortable: true,
      headerComponentParams: {
        displayName: 'Tax',
        enableSorting: true,
        enableMenu: false,
        subText: TABLE_HEADER_SUBTEXT.EXCL_ITTS,
        subText2: TABLE_HEADER_SUBTEXT.ACTUAL_FORECASTED,
      },
      width: 160,
      cellClass: 'cell-text-aligment',
      cellRenderer: MoneyCellRenderer2,
      cellRendererParams: () => {
        const service_line_latest = 'tax_latest';
        const service_line = 'tax';
        return { service_line_latest, service_line };
      },
      suppressMovable: true,
    },
    {
      headerName: 'ITTS',
      field: 'itts_latest',
      headerComponentParams: {
        displayName: 'ITTS',
        enableMenu: false,
        enableSorting: true,
        subText: TABLE_HEADER_SUBTEXT.ACTUAL_FORECASTED,
      },
      width: 160,
      cellClass: 'cell-text-aligment',
      cellRenderer: MoneyCellRenderer2,
      cellRendererParams: () => {
        const service_line_latest = 'itts_latest';
        const service_line = 'itts';
        return { service_line_latest, service_line };
      },
      suppressMovable: true,
    },
    {
      headerName: 'EMEIA ACR',
      field: 'EMIAACR_latest',
      sortable: true,
      headerComponentParams: {
        displayName: 'EMEIA ACR',
        enableMenu: false,
        enableSorting: true,
        subText: TABLE_HEADER_SUBTEXT.ACTUAL_FORECASTED,
      },
      width: 165,
      cellClass: 'cell-text-aligment',
      cellRenderer: MoneyCellRenderer2,
      cellRendererParams: () => {
        const service_line_latest = 'EMIAACR_latest';
        const service_line = 'EMIAACR';
        return { service_line_latest, service_line };
      },
      suppressMovable: true,
    },
    {
      headerName: 'TOV',
      field: 'TOV_latest',
      sortable: true,
      headerComponentParams: {
        displayName: 'TOV',
        enableMenu: false,
        enableSorting: true,
        subText: TABLE_HEADER_SUBTEXT.ACTUAL_FORECASTED,
      },
      width: 160,
      cellClass: 'cell-text-aligment',
      cellRenderer: MoneyCellRenderer2,
      cellRendererParams: () => {
        const service_line_latest = 'TOV_latest';
        const service_line = 'TOV';
        return { service_line_latest, service_line };
      },
      suppressMovable: true,
    },
  ];

  const handleGridReady = params => {
    ({ api: gridApi } = params);
  };

  // Footer Table Functions

  let footerRowData = [];
  if (gcspOppViewData.length !== 0) {
    const rowDataTotalForecasted = gcspOppViewData.PL_Total_Forecasted.map(data => ({
      ...data,
      background: '#3A3A4A',
    }));

    const rowDataTotalYourJudge = gcspOppViewData.Your_Judge.map(data => ({
      ...data,
      background: '#3A3A4A',
    }));
    const rowDataTotalCommit = gcspOppViewData.Total_Forecasted.map(data => ({
      ...data,
      background: '#3A3A4A',
    }));
    const rowDataActualSold = gcspOppViewData.Actual_Sold.map(data => ({
      ...data,
      background: '#1A1A2A',
    }));
    const rowDataAccuarcy = gcspOppViewData.Accuracy.map(data => ({
      ...data,
      background: '#1A1A2A',
    }));
    footerRowData = rowDataTotalForecasted.concat(
      rowDataTotalYourJudge,
      rowDataTotalCommit,
      rowDataActualSold,
      rowDataAccuarcy,
    );
  }

  const TextCellRendererC = props => {
    const { value, data } = props;
    if (value === null) {
      return <TableData1CDark>&nbsp;</TableData1CDark>;
    }

    if (data.category === 'Accuracy') {
      return <TableData1BDark>{value}%</TableData1BDark>;
    } else if (data.category === 'ACTUAL SOLD') {
      return <TableData1BDark>{formateTOV(value)}</TableData1BDark>;
    } else {
      return <TableData1CDark>{formateTOV(value)}</TableData1CDark>;
    }
  };

  TextCellRendererC.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.object.isRequired,
  };

  const ColumnHeaderRenderer = ({ data }) => {
    const keyToText = {
      'PL Total Forecasted': { main_text: 'PL Total Forecasted + Audit/TR Forecast', sub_text: '' },
      'Your Judge': { main_text: 'Your Judge', sub_text: '' },
      'Your Total Forecasted': {
        main_text: 'Your Total Commit',
        sub_text: '(PL Commit+ Audit/TR + Your Judge)',
      },
      'ACTUAL SOLD': { main_text: 'Actual Sold', sub_text: '(All Won)' },
      Accuracy: { main_text: 'Accuracy', sub_text: '(Your Forecasted vs Actual)' },
    };
    const category = data.category;
    const main_displayText = keyToText[category]?.main_text || '';
    const sub_displayText = keyToText[category]?.sub_text || '';

    return (
      <GcspFooterColumnRenderer>
        <TableData1BDark>
          <span>{main_displayText}</span>
        </TableData1BDark>
        <TableData1CDark>
          <span>{sub_displayText}</span>
        </TableData1CDark>
        {category === 'Your Judge' &&
          (!comment || (typeof comment === 'string' && comment.trim() === '') ? (
            <span
              style={{
                marginLeft: '10px',
                color: '#747480',
                fontWeight: 700,
                cursor: 'default',
                textDecoration: 'none',
              }}
            >
              View Comment
            </span>
          ) : (
            <button
              type="button"
              onClick={handleOpenModal}
              style={{
                textDecoration: 'none',
                color: '#155cb4',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                marginLeft: '10px',
                fontSize: '14px',
              }}
            >
              View Comment
            </button>
          ))}
      </GcspFooterColumnRenderer>
    );
  };
  const cellTextAligment = { display: 'flex', justifyContent: 'flex-end', alignItems: 'center' };
  const footerColumnDefs = [
    {
      headerName: '',
      width: 1295,
      cellRenderer: ColumnHeaderRenderer,
      headerComponentParams: {
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'Assurance',
      field: 'Assurance',
      width: 160,
      id: 1,
      cellRenderer: TextCellRendererC,
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Assurance',
        subText: 'excl. ACR',
        subText2: 'Actual vs (Forecasted)',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'Consulting',
      field: 'consulting',
      width: 160,
      cellRenderer: TextCellRendererC,
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Consulting',
        subText: 'Actual vs (Forecasted)',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'SAT',
      field: 'sat',
      width: 160,
      cellRenderer: TextCellRendererC,
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'SAT',
        subText: 'excl. ITTS',
        subText2: 'Actual vs (Forecasted)',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'Tax',
      field: 'tax',
      width: 160,
      cellRenderer: TextCellRendererC,
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Tax',
        subText: 'excl. ITTS',
        subText2: 'Actual vs (Forecasted)',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'ITTS',
      field: 'itts',
      width: 160,
      cellRenderer: TextCellRendererC,
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'ITTS',
        subText: 'Actual vs (Forecasted)',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'EMIA ACR',
      field: 'EMIAACR',
      width: 165,
      cellRenderer: TextCellRendererC,
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'EMEIA ACR',
        subText: 'Actual vs (Forecasted)',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'TOV',
      field: 'TOV',
      width: 160,
      cellRenderer: TextCellRendererC,
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Total',
        subText: 'Actual vs (Forecasted)',
        enableSorting: false,
        enableMenu: false,
      },
    },
  ];

  const getFooterTableRowStyle = params => {
    return { background: params.data.background, border: params.data.background };
  };

  return (
    <>
      <style>
        {`
          #gcspPastFooterTable .titleContainer {
            line-height: 1.2;
          }
          #gcspPastFooterTable .customHeaderTitle {
            padding-left: 10px;
          }
          #gcspPastFooterTable .customHeaderSubText {
            margin-top: 0px;
            padding-left: 8px;
          }
        `}
      </style>
      <GcspPastTableContainer
        className="ag-theme-alpine"
        style={{
          maxWidth: 1920,
          flex: 2,
          height: 365,
        }}
      >
        <AgGridReact
          ref={topGrid}
          alignedGrids={[bottomGrid]}
          firstColumnBorderRight
          columnDefs={columnDefs}
          rowData={rowData}
          groupDisplayType="singleColumn"
          showOpenedGroup={true}
          autoGroupColumnDef={autoGroupColumnDef}
          onGridReady={handleGridReady}
          headerHeight={70}
          headerCellWidth={200}
          components={{
            agColumnHeader: TableHeader,
            pursuitTypeCellRenderer: PursuitTypeCellRenderer,
          }}
          getRowStyle={getRowStyle}
          // overlayNoRowsTemplate={showNowRowsToShowText ? 'Loading...' : 'No Rows To Show'}
        />
      </GcspPastTableContainer>
      <GcspPastFooterTableContainer id="gcspPastFooterTable">
        <AgGridReact
          ref={bottomGrid}
          alignedGrids={[topGrid]}
          rowData={footerRowData}
          columnDefs={footerColumnDefs}
          components={{
            agColumnHeader: TableHeader,
            textCellRendererC: TextCellRendererC,
          }}
          headerHeight={60}
          getRowStyle={getFooterTableRowStyle}
          overlayNoRowsTemplate="Loading..."
        />
      </GcspPastFooterTableContainer>
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                backgroundColor: '#333',
                padding: '15px',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
              }}
            >
              View Comment: {getDisplayTitle()}
            </div>

            {/* Modal Body */}
            <div
              style={{
                padding: '20px',
                fontSize: '14px',
                color: '#555',
                lineHeight: '1.5',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {comment ?? 'No comment available'}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '15px',
                textAlign: 'left',
                borderTop: '1px solid #ddd',
              }}
            >
              <button
                style={{
                  backgroundColor: 'white',
                  color: '#000',
                  border: '1px solid #000',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GcspPastTable;
